import { inject, injectable } from 'inversify';
import sortBy from 'lodash/sortBy';
import { applyToPoint, inverse } from 'transformation-matrix';
import Messager from '../messager';
import { isCanvasSupported } from '../../helpers/canvas';
import { isChromium, isSafari } from '../../helpers/device';
import { createSeat, filterTickets, findSeatNearByCursor } from '../../helpers/seats';
import { transformWidgetSchemeData } from '../../helpers/widget';
import { ITicket } from '../../types/ticket';
import isEmpty from 'lodash/isEmpty';
import widgetSources from '../../sources/widget';
import MESSAGE_TYPES from '../../constants/MESSAGE_TYPES';
import { Settings } from '../../types/settings';
import { CATEGORIES_EVENTS, PRICES_EVENTS } from '../../constants/CONTROLLERS_EVENT_TYPES';
import WidgetSkeleton from '../widget/WidgetSkeleton';
import { TICKETS_MODULE_IDS } from '../widget/tickets/tickets.types';
import type { ITicketsController } from '../widget/tickets/tickets.interface';
import { CALENDAR_MODULE_IDS } from '../widget/calendar/calendar.types';
import { CATEGORIES_MODULE_IDS } from '../widget/categories/categories.module';
import { HELP_MODULE_IDS } from '../widget/help/help.module';
import { NOTIFICATION_MODULE_IDS } from '../widget/notification/notification.module';
import { PRICES_MODULE_IDS } from '../widget/prices/prices.module';
import { POPOVER_MODULE_IDS } from '../widget/popover/popover.types';
import { TICKETS_POPOVER_MODULE_IDS } from '../widget/ticketsPopover/ticketsPopover.module';
import { captureException } from '../../helpers/sentry';
import type { ICalendarController } from '../widget/calendar/calendar.interface';
import type { IPopoverController } from '../widget/popover/popover.interface';
import { validateSchema, validateSettings } from '../../shared/helpers/schema';

// @ts-ignore
const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

@injectable()
export class WidgetCommon extends WidgetSkeleton {
  staticSeats: Array<any> = [];

  currentTickets: Array<any> = [];

  focusedSeat: ITicket;

  isHybrid: boolean = false;

  schemeSupported = isCanvasSupported();

  schemeMode = this.schemeSupported;

  withCategories: boolean = false;

  svgNode: null | SVGAElement = null;

  layout: any;

  @inject(TICKETS_MODULE_IDS.TICKETS_CONTROLLER) public ticketsController: ITicketsController;
  @inject(CALENDAR_MODULE_IDS.CALENDAR_CONTROLLER) public calendarController: ICalendarController;
  @inject(CATEGORIES_MODULE_IDS.CATEGORIES_CONTROLLER) public categoriesController;
  @inject(HELP_MODULE_IDS.HELP_CONTROLLER) public helpController;
  @inject(NOTIFICATION_MODULE_IDS.NOTIFICATION_CONTROLLER) public notificationController;
  @inject(PRICES_MODULE_IDS.PRICES_CONTROLLER) public pricesController;
  @inject(POPOVER_MODULE_IDS.POPOVER_CONTROLLER) public popoverController: IPopoverController;
  @inject(TICKETS_POPOVER_MODULE_IDS.TICKETS_POPOVER_CONTROLLER) public ticketsPopoverController;

  setSettings({ uuid, settings }: { uuid: string; settings: Settings }) {
    super.setSettings({ uuid, settings });

    this.ticketsController.addListener(this.ticketsController.events.ADD, (newTicket) => {
      if (!this.schemeMode) return;
      this.renderSelectedSeats({
        newTicket,
      });
    });
    this.ticketsController.addListener(this.ticketsController.events.REMOVE, (removedTicket) => {
      if (!this.schemeMode) return;
      this.renderSelectedSeats();
      this.renderLeftovers();

      this.renderCategoriesOutlines();

      if (!this.isMobile && removedTicket) {
        createSeat({
          ctx: this.seatsCtx,
          seat: removedTicket,
          hovered: true,
        });
      }
    });
    this.stateController.addListener(this.stateController.events.UPDATE, (state) => {
      if (this.schemeSupported) {
        if (!state.isDragging && !state.isLoading && state.schemeIsLoaded) {
          this.update();
        }
      }
    });
    this.stateController.addListener(
      this.stateController.events.SHCEME_DATA_LOADED,
      this.renderSeatsPoints,
    );
    this.stateController.addListener(
      this.stateController.events.TICKETS_DATA_LOADED,
      this.renderTickets,
    );
    this.ticketsController.addListener(this.ticketsController.events.SET_PRICES, this.setPrices);
    this.categoriesController.addListener(CATEGORIES_EVENTS.SET_PRICES, this.setCategoriesPrices);
    this.pricesController.addListener(PRICES_EVENTS.TOGGLE_PRICE, this.updateSeatsColor);
    this.ticketsController.addListener(
      this.ticketsController.events.RENDER_TICKETS,
      this.renderTickets,
    );
  }

  loadData = async () => {
    this.loadScheme()
      .then(async (schemeData) => {
        const datePrices = await this.loadDatePrices();
        this.metaController.addMeta({
          ...schemeData,
          prices: datePrices,
        });

        await this.onSchemeDataLoad();

        return schemeData;
      })
      .then((schemeData) => {
        return this.loadTickets().then(() => {
          if (isChromium() && this.isMobile) {
            // https://bugs.chromium.org/p/chromium/issues/detail?id=1431968&q=component%3ABlink%3ECanvas&can=2
            this.backgroundController.render();
          }

          this.stateController.emit(
            this.stateController.events.SHCEME_DATA_LOADED,
            schemeData.coordinates,
          );

          this.stateController.emit(this.stateController.events.TICKETS_DATA_LOADED);
          this.backgroundController.init();

          this.stateController.emit(this.stateController.events.DATA_LOADED);

          if (this.shouldRerenderOnLoadData) {
            this.stateController.emit(this.stateController.events.WIDGET_RERENDER);
          }
        });
      })
      .catch((error) => {
        captureException(error);
      });
  };

  loadDatePrices = async () => {
    try {
      const data = await widgetSources.datesPrices();

      return data;
    } catch (error) {
      captureException(error);
    }
  };

  loadScheme = async () => {
    try {
      validateSettings();

      const response = await widgetSources.root();

      validateSchema(response.data);

      const schemeData = transformWidgetSchemeData({
        settings: this.settings,
        schemeData: response.data,
      });
      this.setSchemeParameters(schemeData);

      const { coordinates } = schemeData;
      this.staticSeats = isEmpty(coordinates) ? [] : coordinates;

      return schemeData;
    } catch (error) {
      this.ticketsController.emit(this.ticketsController.events.CHECK_ERROR, error);
    }
  };

  onSchemeDataLoad = async () => {
    if (isSafari()) {
      await this.backgroundController.createSVGBackground();
    } else {
      await this.backgroundController.preloadImageBackground();
    }

    this.backgroundController.render();
  };

  loadTickets = () => this.ticketsController.getTickets();

  toggleFullscreen = () => {
    const { isFullscreen: prevFullScreen } = this.stateController;

    this.stateController.lock();

    this.stateController.setState({
      isFullscreen: !prevFullScreen,
    });

    Messager.send({
      type: MESSAGE_TYPES.FULLSCREEN,
      isFullscreen: this.stateController.state.isFullscreen,
    });
  };

  get ticketsInViewport() {
    if (this.zoomController.zoomIsMinimum) {
      return this.ticketsController.tickets;
    }
    const { x, y, x2, y2 } = this.getViewport();
    return filterTickets({
      tickets: this.ticketsController.tickets,
      x,
      y,
      x2,
      y2,
    });
  }

  findAndRenderSeat({ pageX, pageY, withoutRender = false }) {
    const matrix = this.staticCtx.getTransform();
    const { dpr } = this;
    const point = applyToPoint(inverse(matrix), {
      x: pageX * dpr - this.offsetLeft * dpr,
      y: pageY * dpr - this.offsetTop * dpr,
    });
    const nearTicket = findSeatNearByCursor({ tickets: this.currentTickets, point });

    if (this.focusedSeat) {
      this.renderSelectedSeats();
    }

    if (nearTicket) {
      this.focusedSeat = nearTicket;

      if (!withoutRender) {
        createSeat({
          ctx: this.seatsCtx,
          seat: nearTicket,
          hovered: true,
          isSelected: this.ticketsController.selectedTicketsIds.has(nearTicket.id),
        });
      }

      return this.focusedSeat;
    }

    this.focusedSeat = null;

    return this.focusedSeat;
  }

  renderTickets = () => {
    if (!this.schemeMode) return;

    const { ticketsInViewport } = this;
    const { selectedPrices } = this.pricesController;
    this.currentTickets = ticketsInViewport;
    const { isStadium } = this.schemeParameters;
    const { zoomIsMinimum } = this.zoomController;

    ticketsInViewport.forEach((seat) => {
      createSeat({
        seat,
        selectedPrices,
        zoomIsMinimum,
        isStadium,
        ctx: this.staticCtx,
        active: true,
      });
    });
    this.renderSelectedSeats();
    this.renderLeftovers();

    !this.isHybrid && this.renderCategoriesOutlines();
  };

  renderLeftovers() {
    if (this.zoomController.zoomIsMinimum) {
      this.backgroundController.renderLeftovers(this.ticketsController.leftoversSectors);
    }
  }

  renderSelectedSeats = ({ newTicket } = { newTicket: null }) => {
    this.clearDynamicCtx();
    const {
      selectedEvent,
      meta: { isPeriodical },
    } = this.metaController;
    const { selectedTickets, selectedTicketsIds } = this.ticketsController;
    const filteredSelectedTickets = isPeriodical
      ? selectedTickets.filter((ticket) => ticket.selectedEvent === selectedEvent)
      : selectedTickets;

    sortBy(filteredSelectedTickets, ['row', 'seat']).forEach((seat) => {
      createSeat({
        seat,
        ctx: this.seatsCtx,
        hovered: !this.isMobile && newTicket && seat.id === newTicket.id,
        isSelected: selectedTicketsIds.has(seat.id),
      });
    });

    this.isHybrid && this.renderCategoriesOutlines();
  };

  renderCategoriesOutlines() {
    this.backgroundController.renderCategoriesOutlines();
  }

  updateSeatsColor = () => {
    this.update(true);
  };

  update(onlySeats = false) {
    if (isIE11) {
      return setTimeout(() => {
        this.updateLayout(onlySeats);
      }, 0);
    }
    this.updateLayout(onlySeats);
  }

  updateLayout(onlySeats = false) {
    if (!onlySeats) {
      this.backgroundController.render();
      this.renderSeatsPoints(this.staticSeatsInViewport);
    }

    this.renderTickets();
  }

  setPrices = (prices) => {
    this.pricesController.updatePrices(prices);
  };

  setCategoriesPrices = (prices, colors) => {
    this.pricesController.updateCategoriesPrices(prices, colors);
  };

  setSchemeMode(val) {
    this.schemeMode = val;
    this.stateController.emit(this.stateController.events.SCHEME_MODE_CHANGE);
  }
}
